import React, { useState, useEffect, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { CompletionScreen } from '../components/CompletionScreen';
import { NumberKeypad } from '../components/NumberKeypad';
import '../styles/MathScene.css';
import '../styles/CompletionScreen.css';

export const MathScene: React.FC = () => {
  const [operation, setOperation] = useState<'addition' | 'subtraction'>('addition');
  const [num1, setNum1] = useState(0);
  const [num2, setNum2] = useState(0);
  const [userAnswer, setUserAnswer] = useState('');
  const [step, setStep] = useState(0);
  const [score, setScore] = useState(0);
  const [problemCount, setProblemCount] = useState(0);
  const [isGameComplete, setIsGameComplete] = useState(false);
  const [carsToShow, setCarsToShow] = useState(0);

  const generateNewProblem = useCallback(() => {
    // Randomly choose operation
    const newOperation = Math.random() < 0.5 ? 'addition' : 'subtraction';
    setOperation(newOperation);

    if (newOperation === 'addition') {
      const n1 = Math.floor(Math.random() * 20) + 1;
      const n2 = Math.floor(Math.random() * (21 - n1)) + 1;
      setNum1(n1);
      setNum2(n2);
      setCarsToShow(0);
    } else {
      const n1 = Math.floor(Math.random() * 20) + 1;
      const n2 = Math.floor(Math.random() * n1) + 1;
      setNum1(n1);
      setNum2(n2);
      setCarsToShow(n1);
    }
    setUserAnswer('');
    setStep(0);
  }, []);

  useEffect(() => {
    generateNewProblem();
  }, [generateNewProblem]);

  const handleNextStep = useCallback(() => {
    if (step < 2) {
      setStep(prev => prev + 1);
      if (operation === 'subtraction' && step === 0) {
        setCarsToShow(num1 - num2);
      }
    } else {
      const correctAnswer = operation === 'addition' ? num1 + num2 : num1 - num2;
      if (parseInt(userAnswer) === correctAnswer) {
        setScore(prev => prev + 1);
      }
      setProblemCount(prev => prev + 1);
      if (problemCount === 19) {
        setIsGameComplete(true);
      } else {
        generateNewProblem();
      }
    }
  }, [step, operation, num1, num2, userAnswer, generateNewProblem, problemCount]);

  const handleBackToStart = () => {
    setScore(0);
    setProblemCount(0);
    setIsGameComplete(false);
    generateNewProblem();
  };

  const getStepInstruction = useCallback(() => {
    switch (step) {
      case 0:
        return `Count the ${num1} ${operation === 'addition' ? 'red' : ''} cars.`;
      case 1:
        if (operation === 'addition') {
          return `Now, count the ${num2} blue cars.`;
        } else {
          return `Now, let's take away ${num2} red cars.`;
        }
      case 2:
        return operation === 'addition' 
          ? "Great! Now count all the red and blue cars together and type the total number." 
          : "Great! Count how many red cars are left and type that number.";
      default:
        return '';
    }
  }, [step, num1, num2, operation]);

  const renderCars = useCallback(() => {
    if (operation === 'addition') {
      return (
        <div className="cars-container">
          <div className="car-group">
            {[...Array(num1)].map((_, i) => (
              <motion.div 
                key={`red-${i}`} 
                className="car red-car"
                initial={{ opacity: 0, scale: 0 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ delay: i * 0.1 }}
              >
                🚗
              </motion.div>
            ))}
          </div>
          {step > 0 && (
            <div className="car-group">
              {[...Array(num2)].map((_, i) => (
                <motion.div 
                  key={`blue-${i}`} 
                  className="car blue-car"
                  initial={{ opacity: 0, scale: 0 }}
                  animate={{ opacity: 1, scale: 1 }}
                  transition={{ delay: i * 0.1 }}
                >
                  🚙
                </motion.div>
              ))}
            </div>
          )}
        </div>
      );
    } else {
      return (
        <div className="cars-container">
          <div className="car-group">
            {[...Array(carsToShow)].map((_, i) => (
              <motion.div 
                key={`red-${i}`} 
                className="car red-car"
                initial={{ opacity: 0, scale: 0 }}
                animate={{ opacity: 1, scale: 1 }}
                exit={{ opacity: 0, scale: 0 }}
                transition={{ delay: i * 0.1 }}
              >
                🚗
              </motion.div>
            ))}
          </div>
        </div>
      );
    }
  }, [num1, num2, step, operation, carsToShow]);

  const handleNumberClick = (number: number) => {
    setUserAnswer(prev => `${prev}${number}`);
  };

  const handleDeleteClick = () => {
    setUserAnswer(prev => prev.slice(0, -1));
  };

  const handleSubmitClick = () => {
    if (step === 2) {
      handleNextStep();
    }
  };

  const renderNextButton = () => {
    if (step < 2) {
      return (
        <motion.button
          className="next-button"
          onClick={handleNextStep}
          whileHover={{ scale: 1.05 }}
          whileTap={{ scale: 0.95 }}
        >
          Next Step
        </motion.button>
      );
    }
    return null;
  };

  if (isGameComplete) {
    return (
      <div className="completion-screen">
        <CompletionScreen score={score} />
        <motion.button
          className="back-button"
          onClick={handleBackToStart}
          whileHover={{ scale: 1.05 }}
          whileTap={{ scale: 0.95 }}
        >
          Back to Start
        </motion.button>
      </div>
    );
  }

  return (
    <div className="math-scene-container">
      <div className="math-scene">
        <div className="problem-display">
          <motion.div 
            initial={{ scale: 0 }} 
            animate={{ scale: 1 }} 
            transition={{ type: "spring", stiffness: 260, damping: 20 }}
          >
            {num1} {operation === 'addition' ? '+' : '-'} {num2} = ?
          </motion.div>
        </div>
        <AnimatePresence>{renderCars()}</AnimatePresence>
        <AnimatePresence mode="wait">
          <motion.div
            key={step}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -20 }}
            className="instruction"
          >
            {getStepInstruction()}
          </motion.div>
        </AnimatePresence>
        {renderNextButton()}
        {step === 2 && (
          <div className="answer-section">
            <div className="answer-display">{userAnswer}</div>
            <NumberKeypad
              onNumberClick={handleNumberClick}
              onDeleteClick={handleDeleteClick}
              onSubmitClick={handleSubmitClick}
            />
          </div>
        )}
        <div className="progress">
          Score: {score} / {problemCount}
        </div>
        <div className="operation-info">
          Current operation: {operation === 'addition' ? 'Addition' : 'Subtraction'}
        </div>
        <div className="problem-count">
          Problem: {problemCount + 1}
        </div>
      </div>
      <footer className="app-footer">
        <p>Made by <a href="https://twitter.com/muhammad_o7" target="_blank" rel="noopener noreferrer">Muhammad</a></p>
      </footer>
    </div>
  );
};